import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { BO_PAGES } from "@/lib/dashboard-home/bo-pages";
import type { DestinationContext } from "@/lib/dashboard-home/destinations";
import {
  isRecordShortcutType,
  RECORD_SHORTCUT_ICONS,
} from "@/lib/dashboard-home/record-shortcuts";
import {
  isRecordShortcut,
  type StoredShortcut,
} from "@/lib/dashboard-home/shortcuts";
import { ArrowRight, Pin } from "lucide-react";
import { SHORTCUT_ICONS } from "../shortcut-icons";

/**
 * Onglet « Une fiche » : une fiche s'épingle depuis la fiche elle-même. On
 * explique comment, et on ouvre le bon tableau pour la retrouver.
 */
export const RecordTab = ({
  shortcuts,
  context,
  onOpenPath,
}: {
  shortcuts: StoredShortcut[];
  context: DestinationContext;
  onOpenPath: (path: string) => void;
}) => {
  const vt = useVisibleT();
  const pinned = shortcuts.filter(isRecordShortcut);
  // Chaque tableau correspond à un type de fiche épinglable.
  const tables = BO_PAGES.filter(
    (page) =>
      isRecordShortcutType(page.id) &&
      (!page.available || page.available(context)),
  );
  const steps = [
    vt(
      "bo.home.pin.step1",
      "Ouvrez la fiche voulue (œuvre, multiple, collectionneur…).",
    ),
    vt(
      "bo.home.pin.step2",
      "Cliquez sur « Épingler », sous le titre de la fiche ou dans le menu Actions.",
    ),
    vt(
      "bo.home.pin.step3",
      "La fiche apparaît dans vos raccourcis, prête à être ouverte.",
    ),
  ];

  return (
    <div className="flex flex-col gap-5">
      <ol className="flex list-none flex-col gap-3 rounded-lg bg-slate-50 p-4 pl-4">
        {steps.map((step, index) => (
          <li key={step} className="flex items-start gap-3 text-sm">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
              {index + 1}
            </span>
            <span className="pt-0.5">{step}</span>
          </li>
        ))}
      </ol>

      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {vt("bo.home.pin.findRecord", "Retrouver la fiche dans")}
        </h3>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {tables.map((page) => {
            const Icon = SHORTCUT_ICONS[page.icon];
            return (
              <button
                key={page.id}
                type="button"
                onClick={() => onOpenPath(page.path)}
                className="group flex min-w-0 items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 text-left text-sm transition-colors hover:border-primary hover:bg-brand-50"
              >
                <Icon className="size-4 shrink-0 text-primary" aria-hidden />
                <span className="min-w-0 flex-1">
                  {vt(page.labelKey, page.label)}
                </span>
                <ArrowRight
                  className="size-4 shrink-0 text-slate-400 group-hover:text-primary"
                  aria-hidden
                />
              </button>
            );
          })}
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {vt("bo.home.pin.alreadyPinned", "Fiches déjà épinglées")}
        </h3>
        {pinned.length === 0 ? (
          <p className="text-sm text-slate-500">
            {vt("bo.home.pin.none", "Aucune fiche épinglée pour l’instant.")}
          </p>
        ) : (
          <ul className="flex list-none flex-wrap gap-1.5">
            {pinned.map(({ id, record }) => {
              const Icon = SHORTCUT_ICONS[RECORD_SHORTCUT_ICONS[record.type]];
              return (
                <li
                  key={id}
                  className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-xs"
                >
                  <Icon className="size-3.5 text-primary" aria-hidden />
                  {record.label || vt("bo.home.pin.untitled", "Sans titre")}
                </li>
              );
            })}
          </ul>
        )}
        <p className="flex items-center gap-1.5 text-xs text-slate-500">
          <Pin className="size-3.5" aria-hidden />
          {vt(
            "bo.home.pin.unpinHint",
            "Pour retirer une fiche, supprimez son raccourci ou cliquez à nouveau sur « Épinglée ».",
          )}
        </p>
      </section>
    </div>
  );
};
